import { useEffect, useState } from 'react'
import { Bot, ShieldAlert } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { applicationsApi, type AuthorizeParams } from '@/lib/applicationsApi'
import { describeError } from '@/lib/auth'
import { bits, has, P, PERMISSION_GROUPS } from '@/lib/permissions'
import { guildColor, guildInitials } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import type { AuthorizeInfo } from '@/types/api'
import base from './InvitePage.module.css'
import styles from './AuthorizePage.module.css'
import { Select } from '../Select'

/** Страница /oauth2/authorize: добавить бота на сервер с запрошенными правами (спецификация 6.7). */
export function AuthorizePage({ params }: { params: AuthorizeParams }) {
  const setPendingAuthorize = useAppStore((s) => s.setPendingAuthorize)
  const openGuild = useAppStore((s) => s.openGuild)
  const [info, setInfo] = useState<AuthorizeInfo | null>(null)
  const [guildId, setGuildId] = useState<string>(params.guildId ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    applicationsApi
      .authorizeInfo(params)
      .then((i) => {
        if (cancelled) return
        setInfo(i)
        setGuildId((g) => (g && i.guilds.some((x) => x.id === g) ? g : (i.guilds[0]?.id ?? '')))
      })
      .catch((err: unknown) => { if (!cancelled) setError(describeError(err).message) })
    return () => { cancelled = true }
  }, [params])

  const finish = () => {
    setPendingAuthorize(null)
    history.replaceState({}, '', '/')
  }
  const confirm = async () => {
    if (!info || !guildId) return
    setBusy(true)
    try {
      const r = await applicationsApi.authorize(params, guildId)
      openGuild(r.guild_id)
      finish()
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }

  // вход через Кличат: соглашаемся и уходим обратно в приложение с кодом
  const allowLogin = async () => {
    setBusy(true)
    try {
      const r = await applicationsApi.authorizeCode(params)
      setPendingAuthorize(null)
      location.replace(r.location)
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }

  const login = params.responseType === 'code'
  const mask = bits(params.permissions)
  const requested = PERMISSION_GROUPS.flatMap((g) => g.items.filter((i) => has(mask, P[i.key])))
  const admin = has(mask, P.ADMINISTRATOR)
  const scopes = params.scope.split(/[ +,]+/).filter(Boolean)

  return (
    <div className={base.page}>
      <div className={cx(base.card, styles.card)}>
        {info ? (
          <>
            <span className={base.icon} aria-hidden="true"><Bot size={32} /></span>
            <p className={base.lead}>
              {login
                ? 'Вход через Кличат'
                : info.application.owner
                  ? `${info.application.owner.global_name ?? info.application.owner.username} просит добавить бота`
                  : 'Добавить бота на сервер'}
            </p>
            <h1 className={base.name}>{info.application.name}</h1>
            <p className={base.description}>@{info.bot.username}{info.application.description ? ` · ${info.application.description}` : ''}</p>
            {scopes.includes('bot') ? (
              <>
                <label className={styles.guildLabel} htmlFor="authorize-guild">Добавить на сервер</label>
                {info.guilds.length ? (
                  <div className={styles.guildRow}>
                    {guildId ? <span className={styles.guildIcon} style={{ background: guildColor(guildId) }} aria-hidden="true">{guildInitials(info.guilds.find((g) => g.id === guildId)?.name ?? '')}</span> : null}
                    <Select id="authorize-guild" className={styles.select} value={guildId} options={info.guilds.map((g) => ({ value: g.id, label: g.name }))} onChange={setGuildId} />
                  </div>
                ) : (
                  <p className={base.error}>Нет серверов, где у вас есть право «Управлять сервером».</p>
                )}
                <div className={styles.perms}>
                  <span className={styles.permsTitle}>Бот получит права</span>
                  {requested.length === 0 ? <span className={styles.permItem}>Только базовые права роли @everyone</span> : null}
                  {requested.map((p) => (
                    <span key={p.key} className={cx(styles.permItem, p.key === 'ADMINISTRATOR' && styles.permAdmin)}>
                      {p.key === 'ADMINISTRATOR' ? <ShieldAlert size={14} /> : null} {p.label}
                    </span>
                  ))}
                  {admin ? <span className={styles.adminWarn}>Администратор получает все права, и перекрытия каналов на него не действуют. Добавляйте только ботов, которым доверяете.</span> : null}
                  <span className={styles.permsHint}>Права урежутся до ваших: выдать больше, чем есть у вас, нельзя.</span>
                </div>
              </>
            ) : login ? (
              <>
                <p className={base.lead}>Приложение просит войти через Кличат и увидеть ваш профиль: имя, аватар и id.
                  Оно не получит ваши сообщения, серверы и личные данные.</p>
                {params.redirectUri ? <p className={styles.permsHint}>После согласия вы вернётесь на {new URL(params.redirectUri).host}</p> : null}
              </>
            ) : (
              <p className={base.error}>Этот запрос не добавляет бота: scope «{params.scope}» пока не поддерживается.</p>
            )}
            {error ? <p className={base.error} role="alert">{error}</p> : null}
            {login ? (
              <button type="button" className={base.primary} disabled={busy} onClick={() => void allowLogin()}>{busy ? 'Разрешаем…' : 'Разрешить'}</button>
            ) : (
              <button type="button" className={base.primary} disabled={busy || !guildId || !scopes.includes('bot')} onClick={() => void confirm()}>{busy ? 'Добавляем…' : 'Добавить'}</button>
            )}
          </>
        ) : (
          <>
            <span className={base.icon} aria-hidden="true"><Bot size={32} /></span>
            <h1 className={base.name}>{error ? 'Бота добавить нельзя' : 'Ищем приложение…'}</h1>
            <p className={base.description}>{error ?? 'Секунду.'}</p>
          </>
        )}
        <button type="button" className={base.later} onClick={finish}>{error ? 'Вернуться' : 'Отмена'}</button>
      </div>
    </div>
  )
}
